(() => {
  'use strict';
  if (customElements.get('ws-configurator')) return;

  class WSConfigurator extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) {
        this.initialized = true;
        this.selections = new Map();
        this.busy = false;
        this.pendingVariant = false;
        this.variantSelectionError = false;
        this.button = this.querySelector('[data-add-to-cart]');
        this.error = this.querySelector('[data-config-error]');
        this.notes = this.querySelector('[data-config-notes]');
        this.quantity = this.querySelector('[data-config-quantity]');
        this.currency = this.dataset.currency || 'USD';
        this.sourceCurrency = this.dataset.shopCurrency || this.currency;
        this.rate = this.currency === this.sourceCurrency ? 1 : Number(window.Shopify?.currency?.rate);
        if (window.Shopify?.currency?.active && window.Shopify.currency.active !== this.currency) this.rate = NaN;
        this.money = new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.currency });
        this.currencyScale = 10 ** this.money.resolvedOptions().maximumFractionDigits;
        this.readVariant(this.dataset);
        try {
          this.config = JSON.parse(this.dataset.config || '{}');
          if (typeof this.config === 'string') this.config = JSON.parse(this.config);
          const groups = this.config.option_groups;
          if (!Array.isArray(groups) || !groups.length) throw new Error('Missing options');
          const groupIds = new Set();
          this.groups = groups.map((group, index) => {
            if (typeof group.id !== 'string' || !group.id.trim() || groupIds.has(group.id) ||
              typeof group.label !== 'string' || !Array.isArray(group.inline_options) || !group.inline_options.length) {
              throw new Error('Incomplete option group');
            }
            groupIds.add(group.id);
            const choiceIds = new Set();
            const choices = group.inline_options.map((choice) => {
              const price = Number(choice.price ?? 0);
              const value = typeof choice.value === 'string' || typeof choice.value === 'number' ? String(choice.value) : '';
              if (!value.trim() || choiceIds.has(value) || typeof choice.label !== 'string' || !Number.isFinite(price) || price < 0) throw new Error('Invalid option');
              choiceIds.add(value);
              return { ...choice, value, label: this.decodeEntities(choice.label), price, cents: Math.round(price * 100) };
            });
            return { ...group, label: this.decodeEntities(group.label), choices, required: index < Number(this.dataset.requiredCount ?? 3) || group.required === true };
          });
          if (!Number.isFinite(this.rate) || this.rate <= 0) throw new Error('Currency conversion unavailable');
          const currencyNote = this.querySelector('[data-option-currency-note]');
          if (currencyNote && this.currency !== this.sourceCurrency && this.groups.some((group) => group.choices.some((choice) => /\$\s*\d/.test(choice.label)))) {
            currencyNote.textContent = `Prices in option descriptions use ${this.sourceCurrency}. Estimates use ${this.currency}.`;
            currencyNote.hidden = false;
          }
          this.renderOptions();
          this.addEventListener('click', this.onClick.bind(this));
          this.addEventListener('change', this.onChange.bind(this));
          this.notes?.addEventListener('input', () => this.update());
          this.quantity?.addEventListener('input', () => this.update());
          this.validConfiguration = true;
          this.update();
        } catch (error) {
          this.querySelector('.ws-configurator__options').textContent = 'Configuration is unavailable. Contact us for help with this harness.';
          this.showError('We could not load a reliable configuration price. Please contact us before ordering.');
          if (this.button) this.button.disabled = true;
        }
      }
      if (typeof subscribe === 'function' && !this.unsubscribeVariant) {
        this.unsubscribeVariant = subscribe('variant-change', ({ data }) => {
          const productInfo = this.closest('product-info');
          if (!productInfo || data.sectionId !== (productInfo.dataset.originalSection || productInfo.dataset.section)) return;
          const fresh = data.html?.querySelector(`ws-configurator[data-product-id="${this.dataset.productId}"]`);
          if (fresh) this.readVariant(fresh.dataset);
          else if (data.variant) {
            this.variantId = String(data.variant.id);
            this.baseCents = data.variant.price;
            this.available = data.variant.available;
          }
          this.pendingVariant = false;
          this.variantSelectionError = false;
          if (!this.stalePricing) this.showError('');
          this.update();
        });
        this.unsubscribeOptions = subscribe('option-value-selection-change', ({ data }) => {
          if (!this.closest('product-info')?.contains(data.event.target)) return;
          this.pendingVariant = true;
          if (!this.stalePricing) this.showError('');
          this.update();
        });
      }
    }

    disconnectedCallback() {
      this.unsubscribeVariant?.();
      this.unsubscribeOptions?.();
      this.unsubscribeVariant = null;
      this.unsubscribeOptions = null;
    }

    readVariant(data) {
      const revision = /^[a-f0-9]{64}$/.test(data.pricingRevision || '') ? data.pricingRevision : '';
      if (this.pricingRevision !== undefined && revision !== this.pricingRevision) {
        this.stalePricing = true;
        this.showError('Options or pricing have changed. Refresh this page to review the current configuration.');
      }
      if (!this.stalePricing) this.pricingRevision = revision;
      this.pricingContractEnabled = !this.stalePricing && data.pricingContract === 'v2' && Boolean(revision);
      this.variantId = data.variantId;
      this.baseCents = Number(data.basePriceCents);
      this.available = data.available === 'true';
      if (this.quantity) {
        this.quantity.min = data.quantityMin || '1';
        this.quantity.step = data.quantityStep || '1';
        if (data.quantityMax) this.quantity.max = data.quantityMax;
        else this.quantity.removeAttribute('max');
        if (!this.quantity.value || Number(this.quantity.value) < Number(this.quantity.min)) this.quantity.value = this.quantity.min;
      }
    }

    decodeEntities(text) {
      const decoder = document.createElement('textarea');
      // Decode individual entities once. Raw catalog markup never enters the HTML parser.
      return text.replace(/&(?:#(?:x[\da-f]+|\d+)|[a-z][\da-z]+);/gi, (entity) => {
        decoder.innerHTML = entity;
        return decoder.value;
      });
    }

    escape(text) {
      return String(text).replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);
    }

    format(cents) {
      return this.money.format(cents / 100);
    }

    convertModifier(cents) {
      return Math.round(cents * this.rate * this.currencyScale / 100) * 100 / this.currencyScale;
    }

    selectedEntries() {
      return [...this.selections.entries()].sort(([first], [second]) => first - second);
    }

    renderOptions() {
      this.querySelector('.ws-configurator__options').innerHTML = this.groups.map((group, index) => {
        const id = `${this.id}-group-${index}`;
        const open = group.required || this.dataset.collapseOptional === 'false';
        return `<section class="ws-option-group${open ? ' ws-option-group--open' : ''}" data-group-index="${index}">
          <h3 class="ws-option-group__heading"><button type="button" class="ws-option-group__header" aria-expanded="${open}" aria-controls="${id}-choices" id="${id}-heading">
            <span>${this.escape(group.label)}${group.required ? '<span class="ws-option-group__badge">Required</span>' : ''}</span>
            <span class="ws-option-group__chevron" aria-hidden="true">⌄</span>
          </button></h3>
          <div id="${id}-choices" class="ws-option-group__content"${open ? '' : ' hidden'}>
            <fieldset class="ws-option-group__choices"><legend class="visually-hidden">${this.escape(group.label)}</legend>
            ${group.choices.map((choice, choiceIndex) => `<label class="ws-choice" for="${id}-choice-${choiceIndex}">
              <input type="radio" id="${id}-choice-${choiceIndex}" name="${id}" class="ws-choice__input" value="${choiceIndex}">
              <span class="ws-choice__content"><span>${this.escape(choice.label)}</span>${choice.cents ? `<span class="ws-choice__price">+${this.format(this.convertModifier(choice.cents))}</span>` : ''}</span>
            </label>`).join('')}
            </fieldset>
          </div>
        </section>`;
      }).join('');
    }

    onClick(event) {
      const header = event.target.closest('.ws-option-group__header');
      if (header && this.contains(header)) {
        const group = header.closest('.ws-option-group');
        const open = header.getAttribute('aria-expanded') !== 'true';
        header.setAttribute('aria-expanded', String(open));
        group.classList.toggle('ws-option-group--open', open);
        group.querySelector('.ws-option-group__content').hidden = !open;
      }
      if (event.target.closest('[data-add-to-cart]')) {
        const restrictedHere = this.querySelector('[data-regional-restriction][data-restricted="true"]');
        if (this.dataset.regionalLocked === 'true' || (document.documentElement.dataset.regionalVisitor === 'true' && restrictedHere)) return;
        this.addToCart();
      }
    }

    onChange(event) {
      if (!event.target.matches('.ws-choice__input') || this.busy || this.pendingVariant || this.stalePricing) return;
      const group = event.target.closest('[data-group-index]');
      const index = Number(group.dataset.groupIndex);
      this.selections.set(index, this.groups[index].choices[Number(event.target.value)]);
      group.classList.add('ws-option-group--selected');
      if (!this.variantSelectionError) this.showError('');
      this.update();
    }

    modifierCents() {
      return [...this.selections.values()].reduce((sum, choice) => sum + choice.cents, 0);
    }

    update() {
      if (!this.validConfiguration) return;
      const needsDetails = [...this.selections.values()].some((choice) =>
        choice.requires_details === true || /enter (?:custom )?details|add notes|text box below|enter color in checkout notes/i.test(choice.label)
      );
      if (this.notes) this.notes.required = needsDetails;
      const detailsLabel = this.querySelector('[data-config-notes-label]');
      if (detailsLabel) detailsLabel.textContent = needsDetails ? 'Configuration details (required)' : 'Configuration details (optional)';
      const hasPaidOptions = this.modifierCents() > 0;
      const total = this.baseCents + this.convertModifier(this.modifierCents());
      const quoteRequired = hasPaidOptions && !this.pricingContractEnabled;
      const selectionUnavailable = this.pendingVariant || this.variantSelectionError;
      this.querySelector('[data-total-label]').textContent = hasPaidOptions ? 'Estimated price per harness' : 'Price per harness';
      const quote = this.querySelector('[data-config-quote]');
      quote.hidden = !quoteRequired || selectionUnavailable;
      this.button.hidden = quoteRequired && !selectionUnavailable;
      const rows = [`<div class="ws-configurator__summary-item"><span>Base harness</span><strong>${this.format(this.baseCents)}</strong></div>`];
      let sourceCents = 0;
      let convertedCents = 0;
      const selections = this.selectedEntries().map(([index, choice]) => {
        sourceCents += choice.cents;
        const nextConvertedCents = this.convertModifier(sourceCents);
        const amount = nextConvertedCents - convertedCents;
        convertedCents = nextConvertedCents;
        return { index, choice, amount };
      });
      selections.forEach(({ index, choice, amount }) => {
        rows.push(`<div class="ws-configurator__summary-item"><span>${this.escape(this.groups[index].label)}: ${this.escape(choice.label)}</span><span>${choice.cents ? `+${this.format(amount)}` : 'Included'}</span></div>`);
      });
      this.querySelector('.ws-configurator__summary-items').innerHTML = rows.join('');
      this.querySelector('[data-total-price]').textContent = this.format(total);
      const quantity = Number(this.quantity?.value || 1);
      this.querySelector('[data-cart-price]').textContent = this.format(total * quantity);
      if (quoteRequired) {
        const lines = [
          'Please confirm this harness configuration and its final price.',
          this.dataset.productTitle,
          `${this.dataset.productUrl}?variant=${this.variantId}`,
          `Quantity: ${quantity}`,
          `Estimated price per harness: ${this.format(total)}`,
          `Estimated total: ${this.format(total * quantity)}`,
          '',
          ...selections.map(({ index, choice, amount }) => `${this.groups[index].label}: ${choice.label}${choice.cents ? ` (+${this.format(amount)})` : ''}`),
          '',
          this.notes?.value.trim() ? `Configuration details: ${this.notes.value.trim()}` : '',
        ];
        const salesEmail = this.dataset.salesEmail || '';
        this.querySelector('[data-config-quote-link]').href = `mailto:${encodeURIComponent(salesEmail)}?subject=${encodeURIComponent(`Harness configuration: ${this.dataset.productTitle}`)}&body=${encodeURIComponent(lines.join('\n'))}`;
      }
      const missing = this.groups.filter((group, index) => group.required && !this.selections.has(index)).length;
      const detailsMissing = needsDetails && !this.notes?.value.trim();
      const regionalLocked = this.dataset.regionalLocked === 'true';
      this.querySelectorAll('.ws-choice__input, [data-config-notes], [data-config-quantity]').forEach((input) => {
        input.disabled = this.busy || this.pendingVariant || this.stalePricing;
      });
      const invalidQuantity = this.quantity && !this.quantity.checkValidity();
      this.button.disabled = regionalLocked || quoteRequired || this.busy || selectionUnavailable || this.stalePricing || !this.available || !this.variantId || !Number.isFinite(this.baseCents) || missing > 0 || detailsMissing || invalidQuantity;
      this.button.setAttribute('aria-busy', String(this.busy));
      this.button.querySelector('.ws-configurator__add-text').textContent = this.busy ? 'Adding…' : this.pendingVariant ? 'Updating selection…' : this.stalePricing ? 'Refresh configuration' : this.variantSelectionError ? window.variantStrings?.unavailable || 'Unavailable' : !this.available ? 'Sold out' : missing ? `Select ${missing} required option${missing === 1 ? '' : 's'}` : detailsMissing ? 'Add configuration details' : invalidQuantity ? 'Check quantity' : 'Add to cart';
    }

    showVariantSelectionError(message, retryUrl, retryLabel) {
      if (!this.validConfiguration) return;
      this.pendingVariant = false;
      this.variantSelectionError = true;
      this.variantId = '';
      this.available = false;
      this.update();
      this.showError(message);
      if (this.error && retryUrl) {
        const link = document.createElement('a');
        link.href = retryUrl;
        link.className = 'link underlined-link';
        link.textContent = retryLabel;
        this.error.append(link);
      }
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    async addToCart() {
      this.update();
      if (!this.validConfiguration || this.button.disabled || (this.modifierCents() > 0 && !this.pricingContractEnabled)) return;
      const activeElement = document.activeElement;
      this.busy = true;
      this.showError('');
      this.update();
      const properties = Object.create(null);
      const options = Object.create(null);
      this.selectedEntries().forEach(([index, choice]) => {
        const label = this.groups[index].label;
        const propertyLabel = label.startsWith('_') || ['Configured Total', 'Configuration Details'].includes(label) ? `Option: ${label}` : label;
        properties[propertyLabel] = choice.label;
        options[this.groups[index].id] = choice.value;
      });
      // The pricing function resolves these IDs against its trusted configuration.
      properties._ws_selection = JSON.stringify({ v: 2, revision: this.pricingRevision, options });
      properties._ws_details = this.notes?.value.trim() || '';
      if (this.notes?.value.trim()) properties['Configuration Details'] = this.notes.value.trim();
      const root = window.Shopify?.routes?.root || '/';
      let addUrl = window.routes?.cart_add_url || `${root}cart/add.js`;
      if (!addUrl.endsWith('.js')) addUrl += '.js';
      try {
        const surface = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const cart = surface && ['getSectionsToRender', 'setActiveElement', 'renderContents'].every((method) => typeof surface[method] === 'function') ? surface : null;
        const sections = cart ? cart.getSectionsToRender() : [];
        const body = { id: this.variantId, quantity: Number(this.quantity?.value || 1), properties };
        if (cart && sections.length) {
          body.sections = sections.map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(activeElement === document.body ? this.button : activeElement);
        }
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'This configuration could not be added. Please try again.');
        // The item is already added. An optional subscriber or cart display failure must not invite a duplicate retry.
        try {
          if (typeof publish === 'function') Promise.resolve(publish('cart-update', { source: 'ws-configurator', productVariantId: this.variantId, cartData: result })).catch(() => {});
        } catch (error) {}
        try {
          const ready = cart && sections.length && sections.every((section) => {
            const target = document.getElementById(section.id) || (section.selector && document.querySelector(section.selector));
            const html = result.sections?.[section.id];
            if (!target || typeof html !== 'string') return false;
            if (section.id === 'cart-notification-product' && (typeof result.key !== 'string' || !result.key)) return false;
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const content = section.id === 'cart-notification-product'
              ? parsed.getElementById(`cart-notification-product-${result.key}`)
              : parsed.querySelector(section.selector || '.shopify-section');
            return content && (section.id !== 'cart-drawer' || (content.querySelector('#CartDrawer-Overlay') && content.querySelector('.drawer__inner')));
          });
          if (ready) {
            cart.renderContents(result);
            cart.classList.remove('is-empty');
            this.busy = false;
            this.update();
            return;
          }
        } catch (error) {}
        window.location.assign(window.routes?.cart_url || `${root}cart`);
      } catch (error) {
        this.showError(error.message || 'This configuration could not be added. Please try again.');
        this.busy = false;
        this.update();
      }
    }
  }

  customElements.define('ws-configurator', WSConfigurator);
})();
